import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, Mail, MessageCircle, CheckCircle, Building2, Package, FileCheck, Users, Award, MapPin, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const AuditFirmDelhi = () => {
  const handleCall = () => window.location.href = 'tel:9205514887';
  const handleWhatsApp = () => window.open('https://wa.me/919205514887', '_blank');

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Audit Firm in Delhi | Stock, Internal & Statutory Audit – AudiX</title>
        <meta name="description" content="AudiX is a professional audit firm in Delhi providing statutory audit, internal audit, stock audit and inventory verification services across Delhi NCR and North India." />
        <link rel="canonical" href="https://www.audix.co.in/audit-firm-in-delhi" />
      </Helmet>

      {/* Header */}
      <header className="bg-white shadow-md py-4 px-4 sticky top-0 z-50">
        <div className="container mx-auto max-w-6xl flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-700 hover:text-blue-600">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
          <div className="flex items-center gap-4">
            <Button onClick={handleCall} size="sm" className="bg-blue-600 hover:bg-blue-700">
              <Phone className="h-4 w-4 mr-2" /> Call Us
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white py-20 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Audit Firm in Delhi – Professional Audit Services</h1>
          <p className="text-xl text-slate-200 max-w-3xl mx-auto mb-8">
            AudiX Solutions & Co. is a trusted audit firm in Delhi providing comprehensive audit and assurance services to businesses across Delhi NCR and North India.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={handleCall} size="lg" className="bg-blue-600 hover:bg-blue-700">
              <Phone className="h-5 w-5 mr-2" /> Contact Us
            </Button>
            <Button onClick={handleWhatsApp} size="lg" className="bg-green-600 hover:bg-green-700">
              <MessageCircle className="h-5 w-5 mr-2" /> WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Audit Services in Delhi</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/stock-audit-services-delhi">
              <Card className="h-full hover:shadow-xl transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <Package className="h-12 w-12 text-blue-600 mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Stock Audit Services</h3>
                  <p className="text-slate-600">Independent verification of inventory across warehouses, retail outlets, and distribution centers in Delhi NCR.</p>
                  <p className="mt-4 text-blue-600 font-medium">Learn more →</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/internal-audit-services-delhi">
              <Card className="h-full hover:shadow-xl transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <FileCheck className="h-12 w-12 text-blue-600 mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Internal Audit Services</h3>
                  <p className="text-slate-600">Risk-based internal audits to evaluate governance frameworks and strengthen internal controls.</p>
                  <p className="mt-4 text-blue-600 font-medium">Learn more →</p>
                </CardContent>
              </Card>
            </Link>
            <Link to="/fixed-asset-verification-delhi">
              <Card className="h-full hover:shadow-xl transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <Building2 className="h-12 w-12 text-blue-600 mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Fixed Asset Verification</h3>
                  <p className="text-slate-600">Physical verification and reconciliation of fixed assets ensuring accuracy of records and compliance.</p>
                  <p className="mt-4 text-blue-600 font-medium">Learn more →</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4 bg-slate-100">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Why Choose AudiX as Your Audit Partner</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              'Founded by professionals with 10+ years of audit experience',
              'GST & MSME Registered audit firm',
              'Team comprising CA & CMA professionals',
              'Strong on-ground audit execution capabilities',
              'Ethical, independent, and confidential approach',
              'Experience across multiple locations in India'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-slate-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage Areas */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Service Coverage in Delhi NCR</h2>
          <div className="grid md:grid-cols-4 gap-4">
            {['Delhi', 'Gurugram', 'Noida', 'Faridabad', 'Ghaziabad', 'Greater Noida', 'Manesar', 'Sonipat'].map((city, index) => (
              <div key={index} className="flex items-center gap-2 bg-blue-50 p-4 rounded-lg">
                <MapPin className="h-5 w-5 text-blue-600" />
                <span className="font-medium text-slate-800">{city}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-blue-900 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-4">Get Professional Audit Services in Delhi</h2>
          <p className="text-xl text-blue-100 mb-8">Contact AudiX Solutions & Co. for reliable audit and assurance services.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={handleCall} size="lg" className="bg-white text-blue-900 hover:bg-slate-100">
              <Phone className="h-5 w-5 mr-2" /> 9205514887
            </Button>
            <Button onClick={handleWhatsApp} size="lg" className="bg-green-600 hover:bg-green-700">
              <MessageCircle className="h-5 w-5 mr-2" /> WhatsApp Us
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-8 px-4">
        <div className="container mx-auto max-w-6xl text-center">
          <p className="text-slate-400">© 2025 AudiX Solutions & Co. All rights reserved.</p>
          <Link to="/" className="text-blue-400 hover:text-blue-300 mt-2 inline-block">← Back to Homepage</Link>
        </div>
      </footer>
    </div>
  );
};

export default AuditFirmDelhi;
